import { useRef } from 'react';
import { authenticatedPost, authenticatedDelete } from '../../lib/api/authenticated-fetch.js';
import { generateClientChatId } from '../../lib/utils/client-chat-id.js';

interface ChatHandlersProps {
  apiBaseUrl: string;
  onError: (error: string) => void;
  onSuccess?: () => void;
  entityContext?: {
    entityType: string;
    entityId: string;
    entityDisplayName?: string;
    entityData?: any;
  };
}

/**
 * Consolidated chat CRUD handlers with shared error handling
 */
export function useChatHandlers({ apiBaseUrl, onError, onSuccess, entityContext }: ChatHandlersProps) {
  // Guards against spawning duplicate empty chat rows. A create can be
  // triggered more than once for a single user intent — React 18/19 StrictMode
  // double-invoke in dev, a double-click on "New Chat", or a re-render loop.
  // Because the POST previously carried no chatId, each duplicate minted a fresh
  // server-side id and left an orphan "Entity Chat" row (the "16 rows in ~24s"
  // incident). We now (1) collapse concurrent invocations onto one in-flight
  // request, and (2) send a client-minted stable chatId so the server's
  // idempotent create-by-id returns the SAME row for any retry.
  const inFlightCreateRef = useRef<Promise<void> | null>(null);

  const createChatHandler = async (onChatCreated: (chatId: string) => void, onChatsReload: () => Promise<void>) => {
    if (inFlightCreateRef.current) {
      return inFlightCreateRef.current;
    }

    const run = (async () => {
      try {
        const body: {
          title: string;
          chatId: string;
          entityContext?: ChatHandlersProps['entityContext'];
        } = { title: 'New Chat', chatId: generateClientChatId() };

        // Include entity context if available (for entity-focused chats)
        if (entityContext) {
          body.entityContext = entityContext;
        }

        const data = await authenticatedPost<any>(`${apiBaseUrl}/chats`, body);
        if (data.success) {
          // Update URL to the new chat (this will trigger the sync effect)
          onChatCreated(data.data.chatId);
          await onChatsReload();
          onSuccess?.();
        } else {
          onError(data.error || 'Failed to create chat');
        }
      } catch (err) {
        onError(err instanceof Error ? err.message : 'Failed to create chat');
      }
    })();

    inFlightCreateRef.current = run;
    try {
      await run;
    } finally {
      inFlightCreateRef.current = null;
    }
  };

  const archiveChatHandler = async (
    selectedChatId: string | null,
    selectedChat: any,
    onChatCleared: () => void,
    onUrlCleared: () => void,
    onChatsReload: () => Promise<void>
  ) => {
    if (!selectedChatId || !selectedChat) return;
    try {
      const isArchived = selectedChat.status === 'archived';
      const endpoint = isArchived ? 'unarchive' : 'archive';
      const errorMessage = isArchived ? 'Failed to restore chat' : 'Failed to archive chat';

      const data = await authenticatedPost<any>(`${apiBaseUrl}/chats/${endpoint}?chatId=${encodeURIComponent(selectedChatId)}`, {});
      if (data.success) {
        onChatCleared();
        // Clear chatId from URL (this will trigger the sync effect)
        onUrlCleared();
        await onChatsReload();
        onSuccess?.();
      } else {
        onError(data.error || errorMessage);
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to archive chat');
    }
  };

  const deleteChatHandler = async (
    selectedChatId: string | null,
    onChatCleared: () => void,
    onUrlCleared: () => void,
    onChatsReload: () => Promise<void>
  ) => {
    if (!selectedChatId) return;
    if (!confirm('Are you sure you want to delete this chat? This action cannot be undone.')) return;
    try {
      const data = await authenticatedDelete<any>(`${apiBaseUrl}/chats?chatId=${encodeURIComponent(selectedChatId)}`);
      if (data.success) {
        onChatCleared();
        // Clear chatId from URL (this will trigger the sync effect)
        onUrlCleared();
        await onChatsReload();
        onSuccess?.();
      } else {
        onError(data.error || 'Failed to delete chat');
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to delete chat');
    }
  };

  const restoreChatHandler = async (chatId: string, onChatsReload: () => Promise<void>) => {
    try {
      const data = await authenticatedPost<any>(`${apiBaseUrl}/chats/unarchive?chatId=${encodeURIComponent(chatId)}`, {});
      if (data.success) {
        await onChatsReload();
        onSuccess?.();
      } else {
        onError(data.error || 'Failed to restore chat');
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to restore chat');
    }
  };

  const deleteAllChatsHandler = async (
    chatIds: string[],
    onChatCleared: () => void,
    onUrlCleared: () => void,
    onChatsReload: () => Promise<void>
  ) => {
    if (chatIds.length === 0) return;
    if (!confirm(`Delete all ${chatIds.length} chats? This cannot be undone.`)) return;
    try {
      for (const id of chatIds) {
        const data = await authenticatedDelete<any>(`${apiBaseUrl}/chats?chatId=${encodeURIComponent(id)}`);
        if (!data.success) {
          throw new Error(data.error || `Failed to delete chat ${id}`);
        }
      }
      onChatCleared();
      onUrlCleared();
      await onChatsReload();
      onSuccess?.();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to delete all chats');
    }
  };

  return {
    createChatHandler,
    archiveChatHandler,
    deleteChatHandler,
    restoreChatHandler,
    deleteAllChatsHandler,
  };
}
